import type { FC } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { SvgIconType } from '@thermenresorts/icons';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { clamp } from 'src/core/utils/Numbers';

export const FIRST_PAGE = 1;

interface Props {
	page: number;
	pageCount: number;
	onPageChange: (page: number) => void;
}

const CarouselPagination: FC<Props> = ({ page, pageCount, onPageChange }) => {
	if (pageCount < 2) {
		return null;
	}

	const goTo = (target: number) => onPageChange(clamp(target, FIRST_PAGE, pageCount));

	const isFirstPage = page <= FIRST_PAGE;
	const isLastPage = page >= pageCount;

	return (
		<View style={styles.container}>
			<Pressable
				accessibilityLabel={Translations.previous}
				disabled={isFirstPage}
				style={[styles.button, isFirstPage && styles.buttonDisabled]}
				onPress={() => goTo(page - 1)}
			>
				<SvgIcon name={SvgIconType.chevronLeft} size={Dimens.iconSize8} tintColor={Colors.gray800} />
			</Pressable>
			<Text style={styles.counter}>
				{page} / {pageCount}
			</Text>
			<Pressable
				accessibilityLabel={Translations.next}
				disabled={isLastPage}
				style={[styles.button, isLastPage && styles.buttonDisabled]}
				onPress={() => goTo(page + 1)}
			>
				<SvgIcon name={SvgIconType.chevronRight} size={Dimens.iconSize8} tintColor={Colors.gray800} />
			</Pressable>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		height: Dimens.size30,
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: Colors.goldPale,
		borderRadius: Dimens.borderRadius99,
	},
	button: {
		width: Dimens.size30,
		height: Dimens.size30,
		alignItems: 'center',
		justifyContent: 'center',
	},
	buttonDisabled: {
		opacity: Dimens.opacity38,
	},
	counter: {
		minWidth: Dimens.size30,
		textAlign: 'center',
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize12,
		color: Colors.gray800,
	},
});

export default CarouselPagination;
